import {
  Button,
  Grid,
  IconButton,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Container,
  Modal,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/styles";
import FileSaver from "file-saver";
import { useEffect, useMemo, useReducer, useState } from "react";
import DashboardContainer from "../../components/dashboard-container";
import OrderList from "../../components/order-management-list";
import DownloadIcon from "../../globalStyles/icons/download-icon";
import ExportIcon from "../../globalStyles/icons/export-icon";
import ImportIcon from "../../globalStyles/icons/import-icon";
import AddIcon from "@mui/icons-material/Add";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { isMobile } from "../../globalStyles/mobile";
import buildQuery from "../../utils/query-builder";
import { orderService } from "../../utils/services/orderService";
import { orderExceptionsService } from "../../utils/services/orderExceptionsService";
import { getExceptionsGenerateError } from "../../utils/orderExceptionsDefaults";
import { orderFilterInitialState } from "../../utils/filter-config";
import { useLocation, useNavigate } from "react-router-dom";
import ClientAutocomplete from "../../components/clientAutocomplete";
import ExceptionsParamsModal from "../../components/exceptions-params-modal";
import ExceptionsReportDialog from "../../components/exceptions-report-dialog";
import { toast } from "react-toastify";
import { userService } from "../../utils/services/userService";
import { loginService } from "../../utils/services/loginService.js";

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state.columns.map((x) =>
          x.key === action.payload.key ? { ...x, ...action.payload } : x,
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };
    case "INIT":
      return {
        ...state,
        columns: state.columns.map((x) => {
          if (x.key === "order_date") x.sort = -1;
          return x;
        }),
      };

    default:
      return state;
  }
}

const OrderManagement = () => {
  const [filterData, dispatch] = useReducer(
    filterReducer,
    orderFilterInitialState,
  );
  const [clientFilter, setClientFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(15);
  const clientId = localStorage.getItem("client_id");
  const oms_registered = localStorage.getItem("rl_oms_token");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const theme = useTheme();
  const location = useLocation();
  const defaultColor = "#D13438";
  const primary_color = theme?.palette?.primary?.main || defaultColor;
  const text_color = theme?.palette?.text?.primary || "#FFF";
  const [fetchData, setFetchData] = useState(0);
  const [importOrder, setImportOrder] = useState(false);
  const [orderType, setOrderType] = useState("B2C");
  const [orderTab, setOrderTab] = useState(0);
  const [exceptionsParamsOpen, setExceptionsParamsOpen] = useState(false);
  const [exceptionsSubmitting, setExceptionsSubmitting] = useState(false);
  const [exceptionsError, setExceptionsError] = useState("");
  const [exceptionsReport, setExceptionsReport] = useState(null);
  const [exceptionsReportOpen, setExceptionsReportOpen] = useState(false);
  const navigate = useNavigate();
  const isAdmin = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (user?.role === "admin") return true;
      const fromUser = Array.isArray(user?.roles) ? user.roles : [];
      const fromStorage = JSON.parse(localStorage.getItem("roles") || "[]");
      const roles = fromUser.length ? fromUser : fromStorage;
      return (
        Array.isArray(roles) &&
        roles.some((role) =>
          String(role?.name || role || "")
            .toLowerCase()
            .includes("admin"),
        )
      );
    } catch (_err) {
      return false;
    }
  }, []);

  const fetchOrderData = async (signal) => {
    setLoading(true);
    try {
      const response = await orderService.fetch(
        clientFilter?._id,
        searchValue,
        buildQuery(filterData, page),
        "S2O",
        { signal },
      );
      setDataCount(response.total);
      setOrderData(response.data);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("Fetch error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch({ type: "INIT" });
  }, []);

  useEffect(() => {
    let data = localStorage.getItem("order-filter-data");
    if (location.search.includes("child") && data) {
      localStorage.removeItem("order-filter-data");
      dispatch({
        type: "EXISTING_DATA",
        data: JSON.parse(data),
      });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    fetchOrderData(signal);
    return () => {
      controller.abort();
    };
  }, [filterData, searchValue, page, clientFilter, fetchData]);

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await orderService.exportOrders(
        clientFilter?._id || clientId,
        buildQuery(filterData, page),
      );
      let sliceSize = 1024;
      let byteCharacters = atob(response.file);
      let bytesLength = byteCharacters.length;
      let slicesCount = Math.ceil(bytesLength / sliceSize);
      let byteArrays = new Array(slicesCount);
      for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
        let begin = sliceIndex * sliceSize;
        let end = Math.min(begin + sliceSize, bytesLength);
        let bytes = new Array(end - begin);
        for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
          bytes[i] = byteCharacters[offset].charCodeAt(0);
        }
        byteArrays[sliceIndex] = new Uint8Array(bytes);
      }
      let blob = new Blob(byteArrays, { type: response.contentType });
      FileSaver.saveAs(new Blob([blob], {}), response.filename);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("Fetch error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  const handleImport = () => {
    let response = orderService.fetchRLOMSOrders();
  };
  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedClient(null);
    setImportOrder(false);
    setOrderType("B2C");
  };

  const handleCreateOrder = () => {
    if (importOrder) {
      return navigate("/order-import");
    } else {
      if (!selectedClient && !clientId) {
        toast.warning("Please select a client.");
        return;
      }

      if (!orderType) {
        toast.warning("Please select an order type (M2C or M2O or B2B).");
        return;
      }

      // localStorage.setItem("selected_order_client_id", selectedClient._id);
      handleCloseModal();
      localStorage.setItem(
        "selected_order_client_id",
        selectedClient?._id || clientId,
      );
      localStorage.setItem("orderType", orderType);
      // if (orderType === "M2O") {
      //   navigate(`/m2o-order/create`);
      // } else {
      //   navigate(`/create-order`);
      // }
      if (orderTab === 0) {
        navigate("/create-order");
      } else {
        navigate("/m2o-order/create");
      }
    }
  };
  const handleOpenExceptions = () => {
    const selectedClientId = clientFilter?._id || clientId;
    if (!selectedClientId) {
      toast.warning("Please select a client.");
      return;
    }
    setExceptionsError("");
    setExceptionsParamsOpen(true);
  };

  const handleGenerateExceptions = async (payload) => {
    const selectedClientId = clientFilter?._id || clientId;
    if (!selectedClientId) {
      toast.warning("Please select a client.");
      return;
    }
    setExceptionsSubmitting(true);
    setExceptionsError("");
    try {
      const report = await orderExceptionsService.generate({
        ...payload,
        client_id: selectedClientId,
      });
      setExceptionsReport(report);
      setExceptionsParamsOpen(false);
      setExceptionsReportOpen(true);
    } catch (error) {
      setExceptionsError(getExceptionsGenerateError(error));
    } finally {
      setExceptionsSubmitting(false);
    }
  };

  const handleSaveClientProfile = async (data) => {
    setLoading(true);
    try {
      const response = await userService.fetchUserByEmail(
        data.email,
        data._id,
      );

      let resp = await loginService.login_oms(response);

      localStorage.setItem("rl_oms_token", resp?.token);
      localStorage.setItem("rl_oms_user", JSON.stringify(resp?.user));

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Fetch failed:", error);
    }
  };
  return (
    <DashboardContainer
      searchBarPlaceholder={"Search by Order Id"}
      headerButtons={
        isMobile() ? (
          <IconButton onClick={handleExport}>
            <DownloadIcon fill={primary_color} />
          </IconButton>
        ) : (
          <Grid>
            <Button
              size="small"
              style={{
                textTransform: "none",
                marginRight: 10,
              }}
              variant="contained"
              color="primary"
              onClick={handleOpenModal}
            >
              <AddIcon />
              <Typography
                fontSize="14px"
                fontWeight={500}
                sx={{
                  color: text_color,
                  marginLeft: 1,
                }}
                fontFamily={"poppins"}
              >
                Order
              </Typography>
            </Button>
            {clientId && oms_registered && (
              <>
                <Button
                  size="small"
                  style={{
                    textTransform: "none",
                    marginRight: 10,
                  }}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    // navigate("/order-import");
                    if (clientId && oms_registered) {
                      navigate("/order-import");
                    } else {
                      setModalOpen(true);
                      setImportOrder(true);
                    }
                  }}
                >
                  <ImportIcon fill={"#ffff"} />
                  <Typography
                    fontSize="14px"
                    fontWeight={500}
                    sx={{
                      color: text_color,
                      marginLeft: 1,
                    }}
                    fontFamily={"poppins"}
                  >
                    Import
                  </Typography>
                </Button>
              </>
            )}
            <Button
              size="small"
              style={{
                textTransform: "none",
                marginRight: 10,
              }}
              variant="contained"
              color="primary"
              onClick={handleOpenExceptions}
            >
              <ReportProblemOutlinedIcon fontSize="small" />
              <Typography
                fontSize="14px"
                fontWeight={500}
                sx={{
                  color: text_color,
                  marginLeft: 1,
                }}
                fontFamily={"poppins"}
              >
                Exceptions
              </Typography>
            </Button>
            {isAdmin && (
              <Button
                size="small"
                style={{
                  textTransform: "none",
                  marginRight: 10,
                }}
                variant="contained"
                color="primary"
                onClick={() => navigate("/order-channel-review")}
              >
                <RateReviewOutlinedIcon fontSize="small" />
                <Typography
                  fontSize="14px"
                  fontWeight={500}
                  sx={{
                    color: text_color,
                    marginLeft: 1,
                  }}
                  fontFamily={"poppins"}
                >
                  Channel Review
                </Typography>
              </Button>
            )}
            <Button
              size="small"
              style={{
                textTransform: "none",
              }}
              variant="contained"
              color="primary"
              onClick={handleExport}
            >
              <ExportIcon fill={"#ffff"} />
              <Typography
                fontSize="14px"
                fontWeight={500}
                sx={{
                  color: text_color,
                  marginLeft: 1,
                }}
                fontFamily={"poppins"}
              >
                Export
              </Typography>
            </Button>
          </Grid>
        )
      }
      hideUploadButton={true}
      hideFilterClient={true}
      hideAddButton={true}
      order
      header={"Orders"}
      onSearchData={setSearchValue}
      onClickExport={handleExport}
      onClickRefresh={() => setFetchData(fetchData + 1)}
      onSelectClient={(e) => {
        setClientFilter(e);
      }}
    >
      <Container
        disableGutters
        maxWidth={false}
        style={{ paddingLeft: 0, paddingRight: 0 }}
        sx={{
          maxWidth: "100%",
          paddingLeft: 0,
          paddingRight: 0,
        }}
      >
        <OrderList
          type="M2C"
          data={orderData}
          fetchOrderData={() => setFetchData(fetchData + 1)}
          page={page}
          rowsPerPage={filterData.limit}
          setRowsPerPage={(e) =>
            dispatch({
              type: "UPDATE_LIMIT",
              payload: e,
            })
          }
          setPage={setPage}
          onChangeFilter={handleFilterChange}
          filterData={filterData}
          dataCount={dataCount}
          loading={loading}
        />
      </Container>

      {/* Modal for creating order */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="create-order-modal"
        aria-describedby="modal-to-select-client-and-order-type"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography
            id="create-order-modal"
            variant="h6"
            component="h2"
            gutterBottom
          >
            {importOrder
              ? "Select Client"
              : clientId
                ? ""
                : `Choose Client and Order Type`}
          </Typography>
          {clientId ? (
            <></>
          ) : (
            <ClientAutocomplete
              selectedClient={selectedClient}
              onClientChange={(e) => {
                setSelectedClient(e);
                handleSaveClientProfile(e);
              }}
              width={"100%"}
            />
          )}

          {importOrder ? (
            <></>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Tabs
                value={orderTab}
                onChange={(_, newValue) => setOrderTab(newValue)}
                variant="fullWidth"
                textColor="primary"
                indicatorColor="primary"
                sx={{ mb: 2 }}
              >
                <Tab label="S2O" />
                <Tab label="M2O" />
              </Tabs>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Select Order Type
              </Typography>
              <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={orderType === "B2C"}
                      onChange={() => setOrderType("B2C")}
                      color="primary"
                    />
                  }
                  label="B2C Order"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={orderType === "B2B"}
                      onChange={() => setOrderType("B2B")}
                      color="primary"
                    />
                  }
                  label="B2B Order"
                />
              </Box>
              {/* <FormControlLabel
                control={
                  <Checkbox
                    checked={orderType === "M2C"}
                    onChange={() => setOrderType("M2C")}
                    color="primary"
                  />
                }
                label="M2C Order"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={orderType === "M2O"}
                    onChange={() => setOrderType("M2O")}
                    color="primary"
                  />
                }
                label="M2O Order"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={orderType === "B2B"}
                    onChange={() => setOrderType("B2B")}
                    color="primary"
                  />
                }
                label="B2B Order"
              /> */}
            </Box>
          )}

          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateOrder}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
      <ExceptionsParamsModal
        open={exceptionsParamsOpen}
        onClose={() => !exceptionsSubmitting && setExceptionsParamsOpen(false)}
        onGenerate={handleGenerateExceptions}
        submitting={exceptionsSubmitting}
        error={exceptionsError}
      />
      <ExceptionsReportDialog
        open={exceptionsReportOpen}
        onClose={() => setExceptionsReportOpen(false)}
        report={exceptionsReport}
      />
    </DashboardContainer>
  );
};

export default OrderManagement;
